import { useMemo, useState } from 'react';
import type { Player } from '../../../types/player';
import { PlayerPhoto } from '../../../components/PlayerPhoto';
import { filterTradePool } from '../utils/search';
import { playerTradeValue } from '../../../utils/tradeDesk';

const POSITIONS = ['All', 'QB', 'RB', 'WR', 'TE'] as const;

interface Props {
  players: Player[];
  excludeIds: Array<string | number>;
  onSelect: (player: Player) => void;
}

export function RosterPicker({ players, excludeIds, onSelect }: Props) {
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState<(typeof POSITIONS)[number]>('All');
  const excluded = useMemo(() => new Set(excludeIds.map(String)), [excludeIds]);

  const visible = useMemo(() => {
    const remaining = players.filter((player) => !excluded.has(String(player.id)));
    const byPosition = position === 'All'
      ? remaining
      : remaining.filter((player) => player.position === position);
    return filterTradePool(byPosition, query);
  }, [players, excluded, position, query]);

  return (
    <div className="rounded-xl border border-white/10 bg-black/20">
      <div className="border-b border-white/10 p-3">
        <input
          type="text"
          autoFocus
          className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-zinc-500"
          placeholder="Filter your roster"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          autoComplete="off"
        />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {POSITIONS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setPosition(option)}
              className={`rounded-full border px-2.5 py-1 text-xs ${
                position === option
                  ? 'border-fiesta-yellow bg-fiesta-yellow text-ink'
                  : 'border-white/10 bg-white/5 text-zinc-400 hover:border-white/20'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div className="max-h-80 overflow-y-auto">
        {visible.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-zinc-500">
            {players.length === 0 ? 'No roster loaded.' : 'No one left in this filter.'}
          </p>
        ) : (
          visible.map((player) => (
            <button
              key={String(player.id)}
              type="button"
              onClick={() => onSelect(player)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-white/5"
            >
              <span className="w-8 shrink-0 text-xs tabular-nums text-zinc-500">
                {player.rank ? `#${player.rank}` : '#'}
              </span>
              <PlayerPhoto
                name={player.name}
                position={player.position}
                team={player.team}
                sleeperId={player.id}
                espnId={player.espn_id}
                imageUrl={player.imageUrl}
                className="h-8 w-8 rounded-full object-cover"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-white">{player.name}</span>
                <span className="block text-xs text-zinc-500">
                  {player.position === 'DEF' ? 'D/ST' : player.position} · {player.team}
                </span>
              </span>
              <span className="text-xs text-zinc-400">{playerTradeValue(player).toFixed(1)}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
